import { useFetcher, useLoaderData } from '@remix-run/react'
import { useRef, useState } from 'react'
import { ProductItem } from '~/design-system/components'
import {
  AlertDialog,
  Button,
  Card,
  DialogTrigger,
  HeaderCard,
  Loading,
  Modal,
  Tooltip,
  TooltipTrigger,
} from '~/design-system/primitives'
import GenericCashbackCard from './CashbackCard/GenericCashbackCard'
import HasReachedCashbackLimitCard from './CashbackCard/HasReachedCashbackLimitCard'
import { Delete, Info, ShoppingCart } from '~/icons'
import type { cartLoader } from '~/loaders/cart'
import { useConditionalVisibility } from '~/hooks/useConditionalVisibilityCashbackCard'
import { useIsActiveEnvFlag } from '~/hooks/useIsActiveFlag'

const CASHBACK_LIMIT = 6000

export default function CartList() {
  const { items, user, cashback } = useLoaderData<typeof cartLoader>()

  let ref = useRef<HTMLFormElement>(null)
  const fetcher = useFetcher()
  let [isOpen, setOpen] = useState(false)
  const hasVariantWeightInCart = items.some((item) => item.isVariableWeight) ?? false

  const isCashbackActive = useIsActiveEnvFlag('IS_ACTIVE_CASHBACK')
  const isCorporate = user?.profile?.isCorporate
  const { cashbackBallance, isAccumulating, messagesCashbackCard } = cashback

  const showGenericCard = useConditionalVisibility(
    () => isCashbackActive && !isCorporate && isAccumulating && cashbackBallance.pending < CASHBACK_LIMIT,
  )
  const showHasReachedCashbackLimitCard = useConditionalVisibility(
    () => isCashbackActive && !isCorporate && isAccumulating && cashbackBallance.pending > CASHBACK_LIMIT,
  )

  return (
    <div className="flex flex-col flex-1 self-stretch lg:w-2/4">
      <Card className="gap-1 overflow-hidden " color="primary" data-component="box-cart-list" border>
        <HeaderCard
          description={
            <>
              <span className="flex-1">
                Meus Produtos
                <span className="text-gray-40">
                  (<span className="text-primary-orange">{items.length}</span>)
                </span>
              </span>
              <div className="flex gap-3 items-center">
                <fetcher.Form action="/checkout/cart" className="h-4" method="post" ref={ref}>
                  <input name="actionType" type="hidden" value="clearCart" />
                </fetcher.Form>
                <DialogTrigger>
                  <Button className="p-0 text-gray-40">
                    <Delete className="fill-gray-40" />
                  </Button>
                  <Modal isDismissable>
                    <AlertDialog
                      actionIcon={<Delete className="fill-white mr-1" />}
                      actionLabel="Excluir"
                      title="Confirmação de Exclusão"
                      variant="confirmation"
                      onAction={() => fetcher.submit(ref.current)}
                    >
                      Tem certeza de que deseja excluir todos os produtos do carrinho? Esta ação não pode ser
                      desfeita.
                    </AlertDialog>
                  </Modal>
                </DialogTrigger>
                {hasVariantWeightInCart ? (
                  <TooltipTrigger isOpen={isOpen} onOpenChange={setOpen}>
                    <Button
                      className="p-0"
                      onPress={() => {
                        // behavior on touch devices
                        setOpen(!isOpen)
                      }}
                    >
                      <Info className="fill-gray-40" />
                    </Button>

                    <Tooltip placement="left">
                      <div className="flex flex-row w-[246px] gap-0 items-center p-1">
                        <Info className="fill-white text-base mr-2 leading-none" />

                        <span>
                          Os produtos de peso variável são adicionados ao carrinho com o preço máximo por
                          unidade. No entanto, o valor final cobrado será baseado no peso real do produto após
                          a pesagem.
                        </span>
                      </div>
                    </Tooltip>
                  </TooltipTrigger>
                ) : null}
              </div>
            </>
          }
          icon={<ShoppingCart className="fill-gray-100 text-xl leading-none" />}
        />
        <Card className="p-0 px-2 gap-2 relative  lg:max-h-full lg:h-full" color="primary">
          <div className="w-full h-full max-h-[400px] overflow-y-auto ">
            <ul className="flex flex-col ">
              {items.map((item, index) => (
                <ProductItem isControled={true} item={item} key={item?.uniqueId ?? index} />
              ))}
            </ul>
          </div>
        </Card>
      </Card>

      {showGenericCard && <GenericCashbackCard messagesCard={messagesCashbackCard} />}

      {showHasReachedCashbackLimitCard && <HasReachedCashbackLimitCard messagesCard={messagesCashbackCard} />}

      {fetcher.state != 'idle' ? <Loading /> : null}
    </div>
  )
}
